<template>
    <order-summary-layout :product-store="productStore" :total-price="totalPrice" :search-request="searchRequest">
        <template v-slot>
            <div class="d-flex mb-3">
                <v-img
                    :src="$filters.imageUrl(imageSrc, 600)"
                    :lazy-src="$filters.srcHost('/placeholder.png')"
                    max-width="150"
                    min-width="100"
                    tile
                    @error="imgError = true"
                >
                    <template v-slot:placeholder>
                        <v-row v-show="!imgError && imageSrc" class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="grey-lighten-5" />
                        </v-row>
                    </template>
                </v-img>
                <div class="ma-2 flex-grow-1 flex-shrink-1 text-truncate">
                    <div class="font-weight-bold text-truncate">
                        {{ info.name }}
                    </div>
                    <supplier-name-label v-if="isTO1" :supplier-name="offer.info.supplierName" />
                    <tour-general-info :info="offer.info" :package-tour-info="info" />
                </div>
            </div>
            <div v-if="hotels.length" class="d-flex flex-column my-1">
                <div v-for="hotel in hotels" :key="hotel.name" class="d-flex flex-column text-caption text--secondary">
                    <div class="d-flex">
                        <v-icon size="small" color="primary" start>mdi-domain</v-icon>
                        <span class="font-weight-medium">
                            {{ hotel.name }}
                        </span>
                    </div>
                    <span class="ps-6">
                        {{ `${hotel.cityName}, ${hotel.countryName}` }}
                    </span>
                </div>
            </div>
            <div v-html="info.shortDescription" />
            <TourOfferSubCard
                hide-btn
                :offer="offer"
                :offer-data="offer.info.packageDate"
                :offer-tariff="offer.info.offerInfo.tariffName"
                :title="offer.info?.serviceName || offer.info?.name"
                :secondary-service-name="offer.info?.secondaryServiceName"
                :search-request="searchRequest"
            />
            <v-divider class="my-2" />
            <v-card flat rounded="0" class="pa-0">
                <v-card-text class="pa-0">
                    <search-summary-content :search-request="searchRequest" tourists-label="guest" show-tourists />
                </v-card-text>
            </v-card>
            <template v-if="additionalServices.length">
                <v-divider class="my-3" />
                <div class="mb-2 text-h6">
                    {{ $t('tours.additional_services') }}
                </div>
                <div v-for="service in additionalServices" :key="service.selectedOfferKey">
                    {{ service.name }}
                </div>
            </template>
        </template>
    </order-summary-layout>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import ConditionsLabel from '~/components/snippets/ConditionsLabel.vue'
    import TourGeneralInfo from '@/components/tours/snippets/TourGeneralInfo.vue'
    import OrderSummaryLayout from '@/components/booking/OrderSummaryLayout.vue'
    import SearchSummaryContent from '@/components/parts/SearchSummaryContent.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import TourOfferSubCard from '~/components/tours/search/offers/TourOfferSubCard.vue'

    @Component({
        components: {
            TourOfferSubCard,
            OrderSummaryLayout,
            TourGeneralInfo,
            ConditionsModal,
            ConditionsLabel,
            SearchSummaryContent,
            SupplierNameLabel,
        },
    })
    export class TourOrderSummary extends Vue {
        @Prop({required: true}) offer
        @Prop({default: true}) prebooked
        @Prop({default: true}) searchRequest

        imgError = false

        get productStore() {
            return toursStore
        }

        get additionalServices() {
            return this.offer.entries.reduce((additionalServices, entry) => {
                if (!entry.obligatory && entry.selectedOfferKey) additionalServices.push(entry)

                return additionalServices
            }, [])
        }

        get info() {
            return toursRuntimeStore.packageTourInfo
        }

        get imageSrc() {
            const images = this.info.images || []
            if (!images.length) return null
            const image = images.find(image => image.mainImage)
            return image ? image.url : images[0].url
        }

        get totalPrice() {
            return toursRuntimeStore.totalPrice(this.offer)
        }

        get isTO1() {
            return authStore.isTO1
        }

        get hotels() {
            return this.offer.entries.reduce((hotels, entry) => {
                if (entry.type === 'ACCOMMODATION') {
                    const hotel = entry.offers.find(offer => {
                        return !!offer.rooms.find(
                            room =>
                                room.groupedOffers.findIndex(
                                    groupedOffer => groupedOffer.offerKey === entry.selectedOfferKey
                                ) !== -1
                        )
                    })
                    if (hotel) hotels.push(hotel)
                }

                return hotels
            }, [])
        }
    }

    export default TourOrderSummary
</script>
